import assign from 'object-assign';
import CalendarSurfaceStore from '../store/schema/CalendarSurfaceStore';
import convertLayoutPositionToStyles from '../utils/convertLayoutPositionToStyles';
import getConflictsLayout from '../logic/layouts/getConflictsLayout';
import resolveItemsConflicts from '../logic/resolveItemsConflicts';
import { CalendarSurfaceTransformProps } from '../interfaces/CalendarSurfaceTransformProps';
import { conflictGroupTemplate } from '../Templates';
import { SurfaceItem } from '../interfaces/SurfaceItem';
import { wrapToGridItem } from '../surfaceItems/GridItem';

/**
 * Generates a layout of surface items with conflicting items showing side-by-side ensuring they never visually overlap
 * @param items The collection of items
 * @param store The surface store
 * @param config The surface transform configuration
 * @param itemsConfig The items configuration
 * @param groupIndex The group index
 */
export function createConflictGroupsLayer(
    items: SurfaceItem[],
    store: CalendarSurfaceStore,
    config: CalendarSurfaceTransformProps,
    groupIndex: number,
    makeDraggableCallback: (
        element: JSX.Element,
        groupIndex: number,
        item: SurfaceItem
    ) => JSX.Element
): JSX.Element[] {
    return resolveItemsConflicts(items).map(item => {
        return conflictGroupTemplate(item, () => {
            return getConflictGroupContent(
                item.children,
                store,
                config,
                groupIndex,
                makeDraggableCallback
            );
        });
    });
}

function getConflictGroupContent(
    columnItems: SurfaceItem[],
    store: CalendarSurfaceStore,
    config: CalendarSurfaceTransformProps,
    groupIndex: number,
    makeDraggableCallback: (
        element: JSX.Element,
        groupIndex: number,
        item: SurfaceItem
    ) => JSX.Element
): JSX.Element[] {
    let doesItemHaveConflicts: boolean = columnItems.length > 1; // means the conflict group has multiple items
    return getConflictsLayout(columnItems, store.userOptions.isRTL).map(columnItem => {
        let conflictItem = columnItem.item;
        let layout = assign(config.itemsLayout.getVerticalLayout(conflictItem), columnItem.layout);

        let itemConfig = config.getItemConfig(conflictItem.model);

        let gridItem = wrapToGridItem(
            convertLayoutPositionToStyles(layout),
            conflictItem.key,
            itemConfig,
            conflictItem.model,
            itemConfig && itemConfig.template && itemConfig.template(store, conflictItem.model),
            groupIndex,
            conflictItem.key,
            doesItemHaveConflicts
        );
        return makeDraggableCallback(gridItem, groupIndex, conflictItem);
    });
}
